import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import Drawer from "@/components/Drawer";
import { LoadingState } from "@/components/LoadingState";
import { buildingApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { useOrgId, useIsStaffOrgContext } from "@/hooks/useOrgId";
import { useParams } from "react-router-dom";
import type { BuildingSummary } from "@/types";

const schema = z.object({
  name: z.string().min(1, "Required"),
  address: z.string().min(1, "Required"),
  city: z.string().min(1, "Required"),
  province: z.string().min(1, "Required"),
  postal_code: z.string().min(1, "Required"),
  country: z.string().default("South Africa"),
  building_type: z.string().optional(),
  year_built: z.coerce.number().optional(),
  num_floors: z.coerce.number().optional(),
  total_floor_area_m2: z.coerce.number().optional(),
  description: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

export default function BuildingsPage() {
  const user = useAuthStore((s) => s.user)!;
  const orgId = useOrgId();
  const inStaffCtx = useIsStaffOrgContext();
  const canManage = user.is_staff || !!user.role?.can_manage_buildings;
  const buildingHref = (id: string) =>
    inStaffCtx ? `/staff/organisations/${orgId}/buildings/${id}` : `/buildings/${id}`;
  const [rows, setRows] = useState<BuildingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { country: "South Africa" },
  });

  const load = async () => {
    setLoading(true);
    const data = await buildingApi.list(orgId);
    setRows(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [orgId]);

  const submit = form.handleSubmit(async (values) => {
    try {
      await buildingApi.create(orgId, values);
      toast.success("Building created");
      setDrawerOpen(false);
      form.reset({ country: "South Africa" });
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed to create building");
    }
  });

  const columns: Column<BuildingSummary>[] = [
    {
      key: "name",
      header: "Name",
      render: (b) => (
        <Link to={buildingHref(b.id)} className="text-brand-700 font-medium hover:underline">
          {b.name}
        </Link>
      ),
    },
    { key: "city", header: "City", render: (b) => `${b.city}, ${b.province}` },
    { key: "type", header: "Type", render: (b) => b.building_type ?? "—" },
    { key: "units", header: "Units", render: (b) => b.unit_count },
    { key: "meters", header: "Meters", render: (b) => b.meter_count },
    {
      key: "alerts",
      header: "Open alerts",
      render: (b) =>
        b.open_alert_count > 0 ? (
          <span className="badge bg-red-50 text-red-700">{b.open_alert_count}</span>
        ) : <span className="text-slate-400">0</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (b) =>
        b.is_active ? (
          <span className="badge bg-brand-50 text-brand-800">Active</span>
        ) : (
          <span className="badge bg-slate-100 text-slate-600">Inactive</span>
        ),
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Buildings"
        subtitle={`${rows.length} ${rows.length === 1 ? "building" : "buildings"} in portfolio`}
        actions={
          canManage && (
            <button onClick={() => setDrawerOpen(true)} className="btn-primary" type="button">
              <Plus size={16} /> Add building
            </button>
          )
        }
      />
      {loading ? <LoadingState /> : <DataTable columns={columns} rows={rows} emptyMessage="No buildings yet" />}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Add building"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setDrawerOpen(false)} type="button">Cancel</button>
            <button className="btn-primary" onClick={submit} type="button">Create</button>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="label">Name</label>
            <input className="input" {...form.register("name")} />
            {form.formState.errors.name && <p className="text-xs text-red-600 mt-1">{form.formState.errors.name.message}</p>}
          </div>
          <div>
            <label className="label">Address</label>
            <input className="input" {...form.register("address")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">City</label>
              <input className="input" {...form.register("city")} />
            </div>
            <div>
              <label className="label">Province</label>
              <input className="input" {...form.register("province")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Postal code</label>
              <input className="input" {...form.register("postal_code")} />
            </div>
            <div>
              <label className="label">Country</label>
              <input className="input" {...form.register("country")} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Type</label>
              <input className="input" {...form.register("building_type")} />
            </div>
            <div>
              <label className="label">Floors</label>
              <input type="number" className="input" {...form.register("num_floors")} />
            </div>
            <div>
              <label className="label">Year built</label>
              <input type="number" className="input" {...form.register("year_built")} />
            </div>
          </div>
          <div>
            <label className="label">Floor area (m²)</label>
            <input type="number" step="0.01" className="input" {...form.register("total_floor_area_m2")} />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input" rows={3} {...form.register("description")} />
          </div>
        </form>
      </Drawer>
    </AppLayout>
  );
}
